import { DecoratedFormProps, InjectedFormProps, reduxForm } from 'redux-form';
import { FormError } from 'src/components/redux-form/FormError';
import React, { useState } from 'react';
import {
    emailList,
    maxLength,
    minLength,
    minPhoneLength,
    required,
    Validators,
} from 'src/components/redux-form/utils/validators';
import classNames from 'classnames';
import { FieldInput } from 'src/components/redux-form/fields/FieldInput';
import styles from '../assets/password-recovery-screen.module.scss';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { FieldDate } from '../../../../../components/redux-form/fields/FieldDate';
import { asyncPersistIntakeTelemedicineConsent } from '../../../../../store/actions/intakeSent-actions';
import { push } from 'connected-react-router';
import Router from '../../../../../navigation/router';
import { routes } from '../../../../../navigation';

export interface TelemedicineConsentFormData {
    student: string;
    formId: string;
    isRead: boolean;
    isGiveMyInformedConsent: boolean;
    fullName: string;
    todayDate: string | null;
}

interface TelemedicineConsentFormProps {
    initialValues: any;
    setIsLoader: any;
}

type InjectedProps = InjectedFormProps<TelemedicineConsentFormData, TelemedicineConsentFormProps>;

const validators: Validators<TelemedicineConsentFormData> = {
    isRead: [required({ message: "It's required." })],
    isGiveMyInformedConsent: [required({ message: "It's required." })],
    fullName: [required({ message: 'Full Name is required.' }), maxLength({ max: 255 })],
    todayDate: [required({ message: 'Date is required.' }), maxLength({ max: 255 })],
};

const TelemedicineConsentForm: React.FunctionComponent<InjectedProps & TelemedicineConsentFormProps> = ({
    handleSubmit,
    error,
    dirty,
    submitting,
    initialValues,
    setIsLoader,
}: InjectedProps & TelemedicineConsentFormProps) => {
    return (
        <form onSubmit={handleSubmit} className={classNames(styles.form)}>
            <h2>Informed Consent: Telehealth</h2>
            <div>
                "Telehealth" includes the practice of health care delivery, diagnosis, consultation, treatment using
                interactive audio-video communications. Telehealth may involve the electronic communication of my
                medical/mental healthcare information to other health care practitioners, with my consent. The rights
                stated below supplement those rights I have generally as a patient of the provider:
                <br />
                <br />I have the right to withhold or withdraw consent to telehealth treatment at any time.
                <br />
                <br />
                The laws that protect the confidentiality of my medical/healthcare information also apply to telehealth.
                As such, I understand that the information disclosed by me during the course of my therapy is generally
                confidential. However, there are mandatory exceptions to confidentiality, including reporting child
                abuse and the imminent risk of danger to self or others. If I put my mental state at issue in certain
                legal proceedings, then the provider may be compelled to release otherwise confidential information
                about my evaluation and treatment.
                <br />
                <br />I understand that there are risks and consequences from telehealth, including, but not limited to,
                the possibility, despite reasonable efforts on the part of my provider, that the transmission of my
                medical information could be interrupted or distorted by technical failures or unauthorized persons, and
                that the electronic communication of my medical information could be accessed by unauthorized persons.
                <br />
                <br />I understand that telehealth-based services and care may not be as complete or effective as
                face-to-face services. I also understand that if my provider believes I would be better served by
                in-person treatment, I will be referred to a provider who can provide such services in my area. I
                understand that there are potential risks and benefits associated with any form of treatment, and that
                despite my efforts and the efforts of my provider, my condition may not improve, and in some cases may
                even get worse. I understand that I may benefit from telehealth, but that results cannot be guaranteed
                or assured.
                <br />
                <br />
                As with all medical records, I understand that I have a right to access my medical information and
                copies of medical records of telehealth treatment in accordance with New York State law.
                <br />
                <br />
                We agree to use the video-conferencing platform selected for our virtual sessions, and the provider will
                explain how to use it.
                <br />
                <br />I will need to use a webcam or smartphone during the session.
                <br />
                <br />
                It is important to be in a quiet, private space that is free of distractions (including cellphone or
                other devices) during the session.
                <br />
                <br />
                It is important to use a secure internet connection rather than public/free Wi-Fi.
                <br />
                <br />
                It is important to be on time. If you need to cancel or change your tele-appointment, you must notify
                the provider in advance by phone or email. Office policies apply for late cancellations.
                <br />
                <br />
                We need a back-up plan (e.g., phone number where you can be reached) to restart the session or to
                reschedule it, in the event of technical problems.
                <br />
                <br />
                We need a safety plan that includes at least one emergency contact and the closest ER to your location,
                in the event of a crisis situation.
                <br />
                <br />
                Minors will need the permission of the parent or legal guardian (and their contact information) to
                participate in telehealth sessions.
            </div>
            <FormError error={error} />
            <FieldInput
                name="student"
                type="hidden"
                wrapperConfig={{
                    label: '',
                }}
            />
            <FieldInput
                name="formId"
                type="hidden"
                value={5}
                wrapperConfig={{
                    label: '',
                }}
            />

            <FieldInput
                name="isGiveMyInformedConsent"
                type="checkbox"
                validate={validators.isGiveMyInformedConsent}
                wrapperConfig={{
                    label: 'I hereby give my informed consent for the use of telehealth in my care. *',
                    classes: {
                        inputWrapper: 'customCheckbox customCheckboxWide fixLineHeight ',
                    },
                }}
            />

            <FieldInput
                name="isRead"
                type="checkbox"
                validate={validators.isRead}
                wrapperConfig={{
                    label:
                        'I have read and understand the information provided above. I have discussed it with the provider, and all of my\n' +
                        'questions have been answered to my satisfaction. My signature below indicates my informed and willful consent\n' +
                        'to treatment. *',
                    classes: {
                        inputWrapper: 'customCheckbox customCheckboxWide fixLineHeight ',
                    },
                }}
            />

            <div className={classNames(styles.signBox)}>
                <FieldInput
                    name="fullName"
                    type="text"
                    validate={validators.fullName}
                    wrapperConfig={{
                        label: 'Patient/Parent/Legal Guardian Initials: *',
                    }}
                />
                <FieldDate
                    name="todayDate"
                    type="text"
                    date={true}
                    validate={validators.todayDate}
                    wrapperConfig={{
                        label: 'Date: *',
                    }}
                />

                <div className={classNames(styles.actions)}>
                    <button
                        type="submit"
                        className={classNames('btn', 'btn-success', 'mr-3')}
                        disabled={!dirty || submitting}
                    >
                        <FontAwesomeIcon icon={faSave} /> Confirm and Send
                    </button>
                </div>
            </div>
        </form>
    );
};

export default reduxForm<TelemedicineConsentFormData, TelemedicineConsentFormProps>({
    form: 'telemedicine-consent-form',
    enableReinitialize: true,
    onSubmit: async (
        data: TelemedicineConsentFormData,
        dispatch: Dispatch<any>,
        props: TelemedicineConsentFormProps,
    ) => {
        props.setIsLoader(true);
        await asyncPersistIntakeTelemedicineConsent({ ...data }, dispatch);
    },
    onSubmitSuccess(
        result: any,
        dispatch: Dispatch<any>,
        props: DecoratedFormProps<TelemedicineConsentFormData, TelemedicineConsentFormProps, string>,
    ) {
        props.setIsLoader(false);
        //toast.success(`The form was successfully sent.`);
        dispatch(push(Router.generate(routes.INTAKE_PARENT_FORM_SUCCESS, {})));
    },

    onSubmitFail(errors: { _error: string }) {
        // props.setIsLoader(false);
        if (errors) {
            let field: any = null;
            Object.keys(errors).map((e: any, index: number, array: any) => {
                if (!field) {
                    field = e;
                    return;
                }
                return;
            });
            if (field) {
                const section = document.querySelector(`label[for='${field}']`);
                section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
        if (errors._error) {
            toast.error(errors._error);
        }
    },
})(TelemedicineConsentForm);
